import React, { useState } from 'react';
import { X } from 'lucide-react';

export interface CanonicalKpiOption {
  id: 'plt' | 'oee' | 'vat' | 'inventory_turnover';
  name: string;
  unit: string;
  type: 'lead_time' | 'process_efficiency' | 'cycle_time' | 'custom';
  direction: 'higher_is_better' | 'lower_is_better' | 'target_is_best';
  initialValue: number;
}

export const CANONICAL_KPI_OPTIONS: CanonicalKpiOption[] = [
  {
    id: 'plt',
    name: 'Lead Time de Processo (PLT)',
    unit: 'Dias',
    type: 'lead_time',
    direction: 'lower_is_better',
    initialValue: 0,
  },
  {
    id: 'oee',
    name: 'Eficiência Global do Equipamento (OEE)',
    unit: '%',
    type: 'process_efficiency',
    direction: 'higher_is_better',
    initialValue: 0,
  },
  {
    id: 'vat',
    name: 'Tempo de Agregação de Valor (VAT)',
    unit: 'Minutos',
    type: 'cycle_time',
    direction: 'lower_is_better',
    initialValue: 0,
  },
  {
    id: 'inventory_turnover',
    name: 'Giro de Estoque / Pulmão',
    unit: 'Horas',
    type: 'custom',
    direction: 'target_is_best',
    initialValue: 0,
  },
];

type LinkKpiModalProps = {
  open: boolean;
  projectId: string;
  onClose: () => void;
  /** Callback recebe o KPI + target opcional definido pelo usuário */
  onActivateKpi: (kpi: CanonicalKpiOption, targetValue: number | null) => void | Promise<void>;
  isBinding?: boolean;
};

export const LinkKpiModal: React.FC<LinkKpiModalProps> = ({
  open,
  projectId,
  onClose,
  onActivateKpi,
  isBinding = false,
}) => {
  // Target por KPI (string para permitir campo vazio)
  const [targets, setTargets] = useState<Record<string, string>>({});

  if (!open) return null;

  const handleActivate = (kpi: CanonicalKpiOption) => {
    const raw = targets[kpi.id];
    const parsed = raw != null && raw.trim() !== '' ? Number(raw) : null;
    const target = parsed != null && Number.isFinite(parsed) ? parsed : null;
    onActivateKpi(kpi, target);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-xl rounded border border-slate-700 bg-slate-900 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
          <div>
            <h3 className="text-[13px] font-semibold text-slate-100">Vincular KPI Canônico</h3>
            <p className="mt-0.5 text-[10px] text-slate-400">Projeto: {projectId}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200" aria-label="Fechar">
            <X size={14} />
          </button>
        </div>

        <div className="space-y-2 p-4">
          {CANONICAL_KPI_OPTIONS.map((kpi) => (
            <div key={kpi.id} className="flex items-center gap-3 rounded border border-slate-700 bg-slate-950/80 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12px] font-semibold text-slate-100">{kpi.name}</p>
                <p className="text-[10px] text-slate-400">Unidade: {kpi.unit}</p>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <label className="text-[9px] uppercase tracking-wider text-slate-500">Target ({kpi.unit})</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="any"
                  placeholder="—"
                  value={targets[kpi.id] ?? ''}
                  onChange={(e) => setTargets((prev) => ({ ...prev, [kpi.id]: e.target.value }))}
                  className="w-20 rounded border border-slate-700 bg-slate-900 px-2 py-1 text-right text-[11px] text-slate-100 focus:border-emerald-600 focus:outline-none"
                />
              </div>
              <button
                type="button"
                disabled={isBinding}
                onClick={() => handleActivate(kpi)}
                className="rounded border border-emerald-700 bg-emerald-700 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-emerald-600 disabled:cursor-wait disabled:opacity-60"
              >
                {isBinding ? 'Ativando...' : 'Ativar KPI'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
